import React, { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardBody, Container, Nav, NavItem, NavLink, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import GeneralLedgerService from "../../../Services/generalLedger/GeneralLedgerService";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import LedgerFilters from "./LedgerFilters";
import LedgerTotals from "./LedgerTotals";
import LedgerTable from "./LedgerTable";
import PartiesTab from "./PartiesTab";
import IncomeReconTab from "./IncomeReconTab";
import StatementsTab from "./StatementsTab";
import BankProfilesTab from "./BankProfilesTab";
import CompaniesTab from "./CompaniesTab";
import UploadStatementModal from "./UploadStatementModal";
import { can, PERMISSIONS, readBlobError, saveBlobResponse } from "./glUtils";
import "./general-ledger.scss";

const TABS = [
  { key: "ledger", label: "Ledger" },
  { key: "parties", label: "Parties" },
  { key: "income-recon", label: "Income recon" },
  { key: "statements", label: "Statements" },
  { key: "profiles", label: "Bank profiles" },
  { key: "companies", label: "Companies" },
];

const FILTER_KEYS = [
  "q", "company_id", "bank_profile_id", "currency", "statement_id", "date_from", "date_to", "type",
  "amount_min", "amount_max", "operation_type", "category", "has_note", "has_attachment", "needs_review", "party",
];
const SORTABLE = ["booking_date", "value_date", "debit", "credit", "balance", "label", "operation_type"];

/** Keeps only known, non-empty filters; list filters stay comma-separated, which the API expands. */
export const toApiParams = (filters) => {
  const params = {};
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") params[key] = value;
  });
  return params;
};

export default function GeneralLedgerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exporting, setExporting] = useState(null);

  const tab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "ledger";
  const filters = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);
  const page = Math.max(Number(filters.page) || 1, 1);
  const perPage = [25, 50, 100, 200].includes(Number(filters.per_page)) ? Number(filters.per_page) : 50;
  const sort = SORTABLE.includes(filters.sort) ? filters.sort : "booking_date";
  const direction = filters.direction === "asc" ? "asc" : "desc";

  // Several updates can fire in one tick (the table emits sort + page 1, the search debounce races
  // other filters); building each from the latest params instead of this render's keeps all of them.
  const latestParams = useRef(searchParams);
  latestParams.current = searchParams;
  const writeParams = (next, options) => {
    latestParams.current = next;
    setSearchParams(next, options);
  };

  const updateParams = (patch, resetPage = true) => {
    const next = new URLSearchParams(latestParams.current);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    if (resetPage && !("page" in patch)) next.delete("page");
    writeParams(next, { replace: true });
  };

  const openLedger = (patch) => {
    const next = new URLSearchParams();
    Object.entries(patch).forEach(([key, value]) => next.set(key, value));
    writeParams(next);
  };

  const metaQuery = useQuery({
    queryKey: ["glMeta"],
    queryFn: GeneralLedgerService.getMeta,
    staleTime: 60_000,
  });
  const meta = metaQuery.data;

  const apiParams = { ...toApiParams(filters), page, per_page: perPage, sort, direction };
  const entriesQuery = useQuery({
    queryKey: ["glEntries", apiParams],
    queryFn: () => GeneralLedgerService.getEntries(apiParams),
    enabled: tab === "ledger" && Boolean(meta),
    placeholderData: (previous) => previous,
  });

  const exportEntries = async (format) => {
    setExporting(format);
    try {
      const response = await GeneralLedgerService.exportEntries({ ...toApiParams(filters), sort, direction }, format);
      saveBlobResponse(response, `general-ledger.${format}`);
    } catch (e) {
      toast.error((await readBlobError(e)) || getApiErrorMessage(e, "Export failed."));
    } finally {
      setExporting(null);
    }
  };

  if (metaQuery.isLoading) {
    return (
      <Container fluid className="gl-page py-5 text-center">
        <Spinner />
      </Container>
    );
  }

  if (metaQuery.isError && !meta) {
    return (
      <Container fluid className="gl-page py-5">
        <div className="alert alert-danger">{getApiErrorMessage(metaQuery.error, "The General Ledger is not available for your account.")}</div>
      </Container>
    );
  }

  return (
    <Container fluid className="gl-page">
      <div className="gl-header">
        <div className="d-flex align-items-center gap-4 flex-wrap">
          <h4 className="mb-0">General Ledger</h4>
          <Nav pills className="gl-tabs">
            {TABS.map((t) => (
              <NavItem key={t.key}>
                <NavLink
                  href="#"
                  active={tab === t.key}
                  onClick={(e) => {
                    e.preventDefault();
                    openLedger(t.key === "ledger" ? {} : { tab: t.key });
                  }}
                >
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
        <div className="d-flex gap-2">
          {tab === "ledger" && can(meta, PERMISSIONS.EXPORT) && (
            <>
              <Button color="light" size="sm" disabled={Boolean(exporting)} onClick={() => exportEntries("csv")}>
                {exporting === "csv" ? "Exporting…" : "Export CSV"}
              </Button>
              <Button color="light" size="sm" disabled={Boolean(exporting)} onClick={() => exportEntries("xlsx")}>
                {exporting === "xlsx" ? "Exporting…" : "Export Excel"}
              </Button>
            </>
          )}
          {can(meta, PERMISSIONS.UPLOAD) && (
            <Button color="primary" size="sm" onClick={() => setUploadOpen(true)}>Upload statement</Button>
          )}
        </div>
      </div>

      <Card className="gl-card">
        <CardBody>
          {tab === "ledger" && (
            <>
              <LedgerFilters
                meta={meta}
                filters={filters}
                onChange={(patch) => updateParams(patch)}
                onReset={() => writeParams(new URLSearchParams(), { replace: true })}
              />
              {filters.statement_id && (
                <div className="alert alert-info py-1 mt-2 mb-0 d-flex justify-content-between align-items-center">
                  Showing rows of statement #{filters.statement_id} only.
                  <Button size="sm" color="link" onClick={() => updateParams({ statement_id: "" })}>Show all</Button>
                </div>
              )}
              {filters.party && (
                <div className="alert alert-info py-1 mt-2 mb-0 d-flex justify-content-between align-items-center">
                  <span>
                    Showing movements of <strong>{filters.party_name || filters.party}</strong> only (every bank name grouped under this party).
                  </span>
                  <Button size="sm" color="link" onClick={() => updateParams({ party: "", party_name: "" })}>Show all</Button>
                </div>
              )}
              <div className="my-2">
                <LedgerTotals
                  totals={entriesQuery.data?.totals}
                  loading={entriesQuery.isFetching}
                  failed={entriesQuery.isError}
                />
              </div>
              {entriesQuery.isError && (
                <div className="alert alert-danger">{getApiErrorMessage(entriesQuery.error, "Could not load ledger rows.")}</div>
              )}
              <LedgerTable
                meta={meta}
                rows={entriesQuery.data?.data || []}
                total={entriesQuery.data?.meta?.total || 0}
                loading={entriesQuery.isLoading}
                page={page}
                perPage={perPage}
                sort={sort}
                direction={direction}
                onPageChange={(p) => updateParams({ page: p }, false)}
                onPerPageChange={(size) => updateParams({ per_page: size })}
                onSort={(field, dir) => updateParams({ sort: field, direction: dir })}
              />
            </>
          )}
          {tab === "parties" && <PartiesTab meta={meta} onOpenLedger={openLedger} />}
          {tab === "income-recon" && <IncomeReconTab meta={meta} />}
          {tab === "statements" && <StatementsTab meta={meta} onOpenLedger={openLedger} />}
          {tab === "profiles" && <BankProfilesTab meta={meta} onOpenLedger={openLedger} />}
          {tab === "companies" && <CompaniesTab meta={meta} onOpenLedger={openLedger} />}
        </CardBody>
      </Card>

      {uploadOpen && <UploadStatementModal isOpen meta={meta} toggle={() => setUploadOpen(false)} />}
    </Container>
  );
}
